"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveCharacter = resolveCharacter;
exports.getOpenPlayerGroup = getOpenPlayerGroup;
exports.getFixedPlayerDefinitions = getFixedPlayerDefinitions;
exports.getReservedFixedSlotNames = getReservedFixedSlotNames;
exports.findPlayerDefinitionForSlot = findPlayerDefinitionForSlot;
exports.countJoinedOpenGroupSeats = countJoinedOpenGroupSeats;
exports.getOpenGroupRemaining = getOpenGroupRemaining;
exports.openGroupMinSatisfied = openGroupMinSatisfied;
exports.allocateNextOpenSlotName = allocateNextOpenSlotName;
exports.maxInstanceOrdinalForTemplate = maxInstanceOrdinalForTemplate;
exports.allocateNextInstanceName = allocateNextInstanceName;
exports.resolveSetCurrentCharacterName = resolveSetCurrentCharacterName;
exports.resolveStartingCharacterIdentity = resolveStartingCharacterIdentity;
exports.characterMatchesEligibleList = characterMatchesEligibleList;
exports.listPlayerNamesForTemplate = listPlayerNamesForTemplate;
exports.listInstanceNamesForTemplate = listInstanceNamesForTemplate;
const characterInstanceNames_1 = require("./characterInstanceNames");
/**
 * Resolve an authored template or runtime instance name to the character definition.
 * `Musician#2` → template `Musician`; unknown names → undefined.
 */
function resolveCharacter(characters, name) {
    const trimmed = (name ?? '').trim();
    if (!trimmed) {
        return undefined;
    }
    const exact = characters.find(c => c.name === trimmed);
    if (exact) {
        return exact;
    }
    const parsed = (0, characterInstanceNames_1.parseCharacterInstanceName)(trimmed);
    if (!parsed) {
        return undefined;
    }
    const template = characters.find(c => c.name === parsed.templateName);
    if (template?.kind === 'open') {
        return template;
    }
    return undefined;
}
function getOpenPlayerGroup(players) {
    return players?.find(p => p.kind === 'open');
}
function getFixedPlayerDefinitions(players) {
    return (players ?? []).filter(p => p.kind !== 'open');
}
/** Slot names reserved by fixed (non-open) player definitions. */
function getReservedFixedSlotNames(players) {
    return new Set(getFixedPlayerDefinitions(players).map(p => p.playerName));
}
/**
 * Player definitions that can bind a concrete slot name:
 * fixed def for that name, or the open group when the slot is a dynamic open seat.
 */
function findPlayerDefinitionForSlot(players, slot) {
    const exact = players?.find(p => p.playerName === slot);
    if (exact && exact.kind !== 'open') {
        return exact;
    }
    if (exact?.kind === 'open') {
        // Group id is not a concrete seat name
        return undefined;
    }
    const openGroup = getOpenPlayerGroup(players);
    if (!openGroup) {
        return undefined;
    }
    const reserved = getReservedFixedSlotNames(players);
    if (reserved.has(slot)) {
        return undefined;
    }
    // Runtime open seats use player2, player3, ... (not the group heading)
    if (/^player\d+$/.test(slot) && slot !== 'player0' && slot !== 'player1') {
        return openGroup;
    }
    return undefined;
}
function countJoinedOpenGroupSeats(session, players) {
    const reserved = getReservedFixedSlotNames(players);
    return session.players.filter(p => !reserved.has(p.playerName)).length;
}
function getOpenGroupRemaining(session, players) {
    const openGroup = getOpenPlayerGroup(players);
    if (!openGroup || openGroup.max === undefined) {
        return 0;
    }
    return Math.max(0, openGroup.max - countJoinedOpenGroupSeats(session, players));
}
function openGroupMinSatisfied(session, players) {
    const openGroup = getOpenPlayerGroup(players);
    if (!openGroup) {
        return true;
    }
    const min = openGroup.min ?? 0;
    return countJoinedOpenGroupSeats(session, players) >= min;
}
/**
 * Next free runtime slot for an open group: player2, player3, … skipping reserved fixed names.
 */
function allocateNextOpenSlotName(session, players) {
    const openGroup = getOpenPlayerGroup(players);
    if (!openGroup || getOpenGroupRemaining(session, players) <= 0) {
        return null;
    }
    const reserved = getReservedFixedSlotNames(players);
    const taken = new Set(session.players.map(p => p.playerName));
    for (let n = 2; n < 1000; n++) {
        const slot = `player${n}`;
        if (reserved.has(slot)) {
            continue;
        }
        if (!taken.has(slot)) {
            return slot;
        }
    }
    return null;
}
/** Highest ordinal in use for a template among session players (0 if none). */
function maxInstanceOrdinalForTemplate(session, templateName) {
    let max = 0;
    for (const p of session.players) {
        for (const name of [p.currentCharacter, p.characterName]) {
            const parsed = (0, characterInstanceNames_1.parseCharacterInstanceName)(name);
            if (parsed && parsed.templateName === templateName) {
                max = Math.max(max, parsed.ordinal);
            }
        }
    }
    return max;
}
function allocateNextInstanceName(session, templateName, highWaterMark) {
    const livingMax = maxInstanceOrdinalForTemplate(session, templateName);
    const floor = Math.max(livingMax, highWaterMark ?? 0);
    return (0, characterInstanceNames_1.makeCharacterInstanceName)(templateName, floor + 1);
}
function isInstanceOwnedByOtherPlayer(session, instanceName, exceptPlayerName) {
    return session.players.some(p => p.playerName !== exceptPlayerName &&
        (p.currentCharacter === instanceName || p.characterName === instanceName));
}
/**
 * Resolve what {@link setCurrentCharacter} should store on the player.
 * Open templates bind to a per-seat instance (correlated ordinal when possible).
 */
function resolveSetCurrentCharacterName(session, characters, player, requestedName) {
    const trimmed = (requestedName ?? '').trim();
    if (!trimmed) {
        return { error: 'Character name is empty' };
    }
    const template = resolveCharacter(characters, trimmed);
    if (!template) {
        return { error: `Character '${trimmed}' not found in adventure` };
    }
    if (template.kind !== 'open') {
        // Fixed characters use the authored name as identity
        if ((0, characterInstanceNames_1.isCharacterInstanceName)(trimmed)) {
            return { error: `Character '${trimmed}' is not a valid fixed character instance` };
        }
        return { instanceOrFixedName: template.name, template };
    }
    // Already an instance of this open template
    if ((0, characterInstanceNames_1.isCharacterInstanceName)(trimmed) && (0, characterInstanceNames_1.getCharacterTemplateName)(trimmed) === template.name) {
        if (isInstanceOwnedByOtherPlayer(session, trimmed, player.playerName)) {
            return { error: `Character instance '${trimmed}' is already used by another player` };
        }
        return { instanceOrFixedName: trimmed, template };
    }
    // Open template name → bind/create this player's instance
    const currentParsed = (0, characterInstanceNames_1.parseCharacterInstanceName)(player.currentCharacter);
    if (currentParsed) {
        const correlated = (0, characterInstanceNames_1.makeCharacterInstanceName)(template.name, currentParsed.ordinal);
        if (!isInstanceOwnedByOtherPlayer(session, correlated, player.playerName)) {
            return { instanceOrFixedName: correlated, template };
        }
    }
    const allocated = allocateNextInstanceName(session, template.name);
    return { instanceOrFixedName: allocated, template };
}
/**
 * Starting character identity when creating a player for a definition (fixed or open group).
 * @param highWaterMarkByTemplate optional per-template floors so ordinals never reuse after LEAVE
 */
function resolveStartingCharacterIdentity(session, characters, playerDef, highWaterMarkByTemplate) {
    const template = resolveCharacter(characters, playerDef.startingCharacter);
    if (!template) {
        return { error: `Starting character '${playerDef.startingCharacter}' not found` };
    }
    if (playerDef.kind === 'open' || template.kind === 'open') {
        if (template.kind !== 'open') {
            return { error: `Open player group requires Kind: open starting character` };
        }
        const highWater = highWaterMarkByTemplate?.[template.name];
        return {
            characterName: allocateNextInstanceName(session, template.name, highWater),
            template
        };
    }
    return { characterName: template.name, template };
}
/** Activity eligibility: authored list uses template names; runtime may pass instance names. */
function characterMatchesEligibleList(eligibleCharacterNames, characterName) {
    if (!eligibleCharacterNames || eligibleCharacterNames.length === 0) {
        return true;
    }
    if (eligibleCharacterNames.includes(characterName)) {
        return true;
    }
    const template = (0, characterInstanceNames_1.getCharacterTemplateName)(characterName);
    return eligibleCharacterNames.includes(template);
}
function listPlayerNamesForTemplate(session, templateName) {
    const template = templateName.trim();
    return session.players
        .filter(p => (0, characterInstanceNames_1.getCharacterTemplateName)(p.currentCharacter) === template)
        .map(p => p.playerName);
}
function listInstanceNamesForTemplate(session, templateName) {
    const template = templateName.trim();
    return session.players
        .filter(p => (0, characterInstanceNames_1.getCharacterTemplateName)(p.currentCharacter) === template)
        .map(p => p.currentCharacter);
}
//# sourceMappingURL=openCharacterRuntime.js.map